<template>
    <div
        v-show="isVisible"
        class="MorphingGhost"
        :class="{ 'is-relative': useRelative }"
        ref="ghost"
        :style="ghostStyle"
    ></div>
</template>

<script>
import { gsap } from 'gsap';

const SPEEDS = {
    slowest: 1.0,
    slow: 0.8,
    normal: 0.6,
    fast: 0.4,
    fastest: 0.3,
};

export default {
    name: 'MorphingGhost',
    props: {
        fromElement: {
            type: [HTMLElement, Object],
            default: null,
        },
        toElement: {
            type: [HTMLElement, Object],
            default: null,
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
        duration: {
            type: Number,
            default: 0.6,
        },
        zIndex: {
            type: Number,
            default: 9999,
        },
        speed: {
            type: String,
            default: 'normal',
            validator: (v) => Object.keys(SPEEDS).includes(v),
        },
        useRelative: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isVisible: false,
            ghostStyle: {},
        };
    },
    computed: {
        currentDuration() {
            return SPEEDS[this.speed] || this.duration;
        },
    },
    watch: {
        isOpen: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.animateOpen();
                } else {
                    this.animateClose();
                }
            },
        },
    },
    methods: {
        getStyles(el) {
            if (!el) return {};
            const rect = el.getBoundingClientRect();
            const computed = window.getComputedStyle(el);

            let top = rect.top;
            let left = rect.left;

            // If we are in relative mode, we subtract the parent's viewport coordinates
            // to get coordinates matching the local stacking context.
            if (this.useRelative) {
                const parent = this.$el?.parentElement;
                if (parent) {
                    const parentRect = parent.getBoundingClientRect();
                    top -= parentRect.top;
                    left -= parentRect.left;
                }
            }

            return {
                top,
                left,
                width: rect.width,
                height: rect.height,

                borderWidth: computed.borderTopWidth || computed.borderWidth,
                borderStyle: computed.borderTopStyle || computed.borderStyle,
                borderColor: computed.borderTopColor || computed.borderColor,
                borderRadius: computed.borderRadius,
                backgroundColor: computed.backgroundColor,
                backgroundImage: computed.backgroundImage,
                boxShadow: computed.boxShadow,
            };
        },
        animateOpen() {
            if (!this.fromElement || !this.toElement) return;

            this.isVisible = true;

            this.$nextTick(() => {
                const startStyles = this.getStyles(this.fromElement);
                const endStyles = this.getStyles(this.toElement);

                this.ghostStyle = {
                    position: this.useRelative ? 'absolute' : 'fixed',
                    zIndex: this.zIndex,
                    ...startStyles,

                    backgroundColor: endStyles.backgroundColor,
                    backgroundImage: endStyles.backgroundImage,
                    borderStyle: endStyles.borderStyle,
                    borderColor: endStyles.borderColor,

                    transform: 'none',
                    margin: 0,
                    pointerEvents: 'none',
                };

                gsap.fromTo(
                    this.$refs.ghost,
                    {
                        top: startStyles.top,
                        left: startStyles.left,
                        width: startStyles.width,
                        height: startStyles.height,
                        borderRadius: startStyles.borderRadius,
                        boxShadow: startStyles.boxShadow,
                        borderWidth: startStyles.borderWidth,

                        backgroundColor: endStyles.backgroundColor,
                        backgroundImage: endStyles.backgroundImage,
                        borderStyle: endStyles.borderStyle,
                        borderColor: endStyles.borderColor,
                    },
                    {
                        top: endStyles.top,
                        left: endStyles.left,
                        width: endStyles.width,
                        height: endStyles.height,
                        borderRadius: endStyles.borderRadius,
                        backgroundColor: endStyles.backgroundColor,
                        backgroundImage: endStyles.backgroundImage,
                        boxShadow: endStyles.boxShadow,
                        borderWidth: endStyles.borderWidth,
                        borderStyle: endStyles.borderStyle,
                        borderColor: endStyles.borderColor,
                        duration: this.currentDuration,
                        ease: 'expo.out',
                        onComplete: () => {
                            this.isVisible = false;
                            this.$emit('animation-complete', 'open');
                        },
                    },
                );
            });
        },
        animateClose() {
            this.isVisible = false;
        },
    },
};
</script>

<style>
.MorphingGhost {
    display: block;
    box-sizing: border-box;
    position: fixed;
    transform-origin: top left;
}

.MorphingGhost.is-relative {
    position: absolute;
}
</style>
